/**
 * Agapaé AI assistant API service — couple-only planning companion.
 *
 * JSON endpoints follow the imperative pattern used elsewhere in app/
 * (typed Promise, ApiError via parseApiError on non-2xx).
 *
 * streamAssistantMessage is the one streaming call in the app: the reply
 * arrives as Server-Sent Events over a POST (EventSource can't send an
 * Authorization header, so we read the fetch body manually).
 *
 * Retry semantics (deliberate — do not "simplify" back to default fetchWithAuth):
 * - `skipRetry: true` disables fetchWithAuth's built-in loops. The default
 *   429 handler retries up to 10× with backoff, which would silently stall
 *   the chat instead of showing the friendly rate-limit message.
 * - Because `skipRetry` also disables the 401 refresh, we do ONE manual
 *   token refresh + retry here.
 *
 * BE routes: /api/events/:eventId/assistant/* — see api/routes/assistant.routes.mjs.
 */

import {
  ApiError,
  fetchWithAuth,
  getWithAuth,
  parseApiError,
  postWithAuth,
  refreshAccessToken,
} from 'utils/api-client';
import { CODES } from 'services/messageCodes';
import { clientTimeZone, clientToday } from 'utils/civilDate';

/** The couple's day and time zone: Éros counts and dates from them, as the Planning Hub does. */
const coupleDay = () => `today=${clientToday()}&tz=${encodeURIComponent(clientTimeZone())}`;

// ── Wire types ────────────────────────────────────────────────────────

/**
 * What one write tool actually did — straight from the tool's own result, never
 * from the reply text. The prose is Éros's claim; this is the outcome, and on a
 * refused write `ok` is false however confidently the text reads.
 */
export interface WriteReceipt {
  /** BE tool name — the FE maps it to copy (generic fallback if unmapped). */
  tool: string;
  /** The executor's coded status ("applied", "write_limit_reached", …). */
  status: string;
  ok: boolean;
  /** Render params the executor chose to surface, e.g. { seated: 31 }. */
  params?: Record<string, unknown>;
}

export interface AssistantMessageDto {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  /**
   * Writes this turn actually performed. Absent on turns with no writes AND on
   * legacy turns predating the field — in both cases nothing is claimed.
   */
  receipts?: WriteReceipt[];
}

export interface AssistantConversationResponse {
  conversationId: string;
  messages: AssistantMessageDto[];
}

/** One history-list entry (GET /assistant/conversations). */
export interface AssistantConversationSummaryDto {
  id: string;
  /** Background-generated label; null → fall back to `preview`. */
  title: string | null;
  /** First-message excerpt (~80 chars) for untitled conversations. */
  preview: string | null;
  lastMessageAt: string | null;
  messageCount: number;
  isActive: boolean;
}

export interface AssistantConversationsResponse {
  conversations: AssistantConversationSummaryDto[];
}

export interface AssistantInsightDto {
  /** Rule id — FE localizes via `assistant.insights.<id>` */
  id: string;
  params: Record<string, unknown>;
  priority: number;
}

export interface AssistantInsightsResponse {
  countdownDays: number | null;
  hasDate: boolean;
  insights: AssistantInsightDto[];
  /** Chip ids — FE localizes via `assistant.chips.<id>` */
  chips: string[];
}

export interface StreamResult {
  messageId: string | null;
  stopReason: string | null;
}

/** A live tool activity announced by the BE ("read" lookup vs "write" action). */
export interface ToolActivity {
  name: string;
  kind: 'read' | 'write';
}

/**
 * A tool that FINISHED. The `tool` event announces invocation, not success — a
 * refused write (e.g. the per-turn write limit) still emits one — so `ok: false`
 * is the only signal that the action did not happen.
 *
 * Carries the same fields as the persisted row, so a receipt rendered live and
 * one rendered after a refresh always say the same thing.
 */
export interface ToolOutcome {
  name: string;
  kind: 'read' | 'write';
  ok: boolean;
  status: string;
  params?: Record<string, unknown>;
}

/** The companion-usage bar — how much of the budget is used, as a percent. */
export interface AssistantUsageBar {
  usedPct: number;
}

/**
 * Couple-facing companion usage — a single lifetime bar for this event's
 * included planning-companion budget. `lifetime` is null when the plan has no
 * budget configured. Percentage only — never carries euros, credits, or the
 * budget value (the premium "no quotas surfaced" rule).
 */
export interface AssistantUsageResponse {
  lifetime: AssistantUsageBar | null;
}

// ── JSON endpoints ────────────────────────────────────────────────────

export async function getAssistantConversation(
  eventId: string,
): Promise<AssistantConversationResponse> {
  const res = await getWithAuth(`/api/events/${eventId}/assistant/conversation`);
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

export async function getAssistantInsights(eventId: string): Promise<AssistantInsightsResponse> {
  const res = await getWithAuth(`/api/events/${eventId}/assistant/insights?${coupleDay()}`);
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

export async function getAssistantUsage(eventId: string): Promise<AssistantUsageResponse> {
  const res = await getWithAuth(`/api/events/${eventId}/assistant/usage`);
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * True when an error is the "event AI budget spent" block (code 119011) — the
 * signal to show the premium "you've made the most of your companion" state
 * instead of a generic error notice.
 */
export function isBudgetExhaustedError(err: unknown): boolean {
  return err instanceof ApiError && err.code === CODES.AI_BUDGET_EXHAUSTED;
}

export async function resetAssistantConversation(eventId: string): Promise<void> {
  const res = await postWithAuth(`/api/events/${eventId}/assistant/conversation/reset`);
  if (!res.ok) throw await parseApiError(res);
}

/** Past + current conversations, newest first (empty ones never listed). */
export async function listAssistantConversations(
  eventId: string,
): Promise<AssistantConversationsResponse> {
  const res = await getWithAuth(`/api/events/${eventId}/assistant/conversations`);
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

/**
 * Resume a past conversation: the BE archives the current active one and
 * re-activates the target, returning it in the exact GET /conversation shape
 * so the caller can swap the thread without a second request. 409s while a
 * reply is streaming (AI_REPLY_IN_PROGRESS).
 */
export async function activateAssistantConversation(
  eventId: string,
  conversationId: string,
): Promise<AssistantConversationResponse> {
  const res = await postWithAuth(
    `/api/events/${eventId}/assistant/conversations/${conversationId}/activate`,
  );
  if (!res.ok) throw await parseApiError(res);
  return res.json();
}

export type AssistantFeedbackRating = 'up' | 'down';

export async function sendAssistantFeedback(
  eventId: string,
  messageId: string,
  rating: AssistantFeedbackRating,
  text?: string,
): Promise<void> {
  const res = await postWithAuth(
    `/api/events/${eventId}/assistant/messages/${messageId}/feedback`,
    { rating, ...(text ? { text } : {}) },
  );
  if (!res.ok) throw await parseApiError(res);
}

// ── Streaming chat turn ───────────────────────────────────────────────

interface StreamOptions {
  eventId: string;
  content: string;
  /** BE adds a reply-language hint to the prompt ("en" | "el"). */
  locale?: string;
  signal?: AbortSignal;
  /** Called for every incremental text chunk of the reply. */
  onDelta: (text: string) => void;
  /** Called when the assistant starts a data lookup / confirmed action. */
  onTool?: (activity: ToolActivity) => void;
  /** Called when that tool finished — `ok: false` means it did not happen. */
  onToolResult?: (outcome: ToolOutcome) => void;
  /**
   * Fired once the server has responded 200 and the SSE stream is open. By
   * then the BE has already persisted the user turn (it's appended atomically
   * with the streaming-lock claim, just before flushHeaders). The caller uses
   * this to decide, on a later error, whether the user message survives: a
   * failure BEFORE this fired stored nothing (roll back); one AFTER it — incl.
   * a refusal with no partial text — means the user turn IS stored (keep it).
   */
  onStreamStart?: () => void;
}

/**
 * Send one message and stream the assistant's reply.
 *
 * Resolves once the reply completes; rejects with ApiError on pre-stream
 * failures (rate limit, validation, plan gate) and on mid-stream `error`
 * events. An abort via `signal` rejects with the DOMException AbortError.
 */
export async function streamAssistantMessage({
  eventId,
  content,
  locale,
  signal,
  onDelta,
  onTool,
  onToolResult,
  onStreamStart,
}: StreamOptions): Promise<StreamResult> {
  const doFetch = () =>
    fetchWithAuth(`/api/events/${eventId}/assistant/messages?${coupleDay()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, locale }),
      signal,
      skipRetry: true,
    });

  let res = await doFetch();

  // One manual 401 refresh (skipRetry disabled the built-in one).
  if (res.status === 401) {
    try {
      await refreshAccessToken();
      res = await doFetch();
    } catch {
      // fall through — the 401 below becomes the surfaced error
    }
  }

  if (!res.ok || !res.body) {
    throw await parseApiError(res);
  }

  // 200 + open stream → the user turn is committed server-side from here on.
  onStreamStart?.();
  return parseSseStream(res.body, onDelta, onTool, onToolResult);
}

/**
 * Minimal SSE parser over a fetch body. Frames are separated by a blank
 * line; `: ping` comment heartbeats are ignored. Unknown events are ignored,
 * so a BE that emits new ones stays compatible with an older FE.
 */
async function parseSseStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (text: string) => void,
  onTool?: (activity: ToolActivity) => void,
  onToolResult?: (outcome: ToolOutcome) => void,
): Promise<StreamResult> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: StreamResult | null = null;

  const handleFrame = (frame: string) => {
    const event = /^event: (.+)$/m.exec(frame)?.[1];
    const data = /^data: (.+)$/m.exec(frame)?.[1];
    if (!event || !data) return; // comment / heartbeat
    if (event === 'delta') {
      onDelta(JSON.parse(data).text ?? '');
    } else if (event === 'tool') {
      const parsed = JSON.parse(data);
      onTool?.({
        name: parsed.name ?? '',
        kind: parsed.kind === 'write' ? 'write' : 'read',
      });
    } else if (event === 'tool_result') {
      const parsed = JSON.parse(data);
      onToolResult?.({
        name: parsed.name ?? '',
        kind: parsed.kind === 'write' ? 'write' : 'read',
        ok: parsed.ok === true,
        status: parsed.status ?? 'ok',
        params: parsed.params,
      });
    } else if (event === 'done') {
      const parsed = JSON.parse(data);
      result = {
        messageId: parsed.messageId ?? null,
        stopReason: parsed.stopReason ?? null,
      };
    } else if (event === 'error') {
      // Same contract as HTTP errors: an ApiError carrying the BE message
      // code, localized by call sites via translateApiMessage.
      const code = JSON.parse(data).code ?? 'GENERIC';
      throw new ApiError(502, code, 'The assistant is temporarily unavailable.');
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        handleFrame(frame);
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!result) {
    // Stream ended without a `done` (connection cut) — surface as unavailable.
    throw new ApiError(502, 'GENERIC', 'The assistant is temporarily unavailable.');
  }
  return result;
}
